import { cn } from '../../lib/cn';

export interface RangeScaleMarker {
  key: string;
  value: number;
  label: string;
}

export interface RangeScaleProps {
  min: number;
  max: number;
  markers: readonly RangeScaleMarker[];
  minLabel: string;
  maxLabel: string;
  className?: string;
}

/**
 * A plain track with one marker per scenario (plan §2.6). No gradient and no fill: the point is *where
 * the four benchmarks sit relative to each other*, and a coloured bar would imply a judgement about the
 * span that the platform is not making.
 *
 * The track is `dir="ltr"` even in Arabic — a number line runs low to high, and mirroring it would put
 * the cheapest scenario on the right while the amounts beside it still read left to right.
 */
export const RangeScale = ({ min, max, markers, minLabel, maxLabel, className }: RangeScaleProps) => {
  const span = Math.max(1, max - min);
  const positionOf = (value: number): number => Math.max(2, Math.min(98, ((value - min) / span) * 100));

  return (
    <div
      dir="ltr"
      className={cn('space-y-2', className)}
    >
      <div className="relative h-2 rounded-full bg-muted">
        {markers.map((marker) => (
          <span
            key={marker.key}
            title={marker.label}
            style={{ left: `${positionOf(marker.value)}%` }}
            className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-xs tabular-nums text-muted-foreground">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
};
